/**
 * Request `items` in batches of at most `size` and merge the responses.
 *
 * Several repository RPCs reject arrays longer than a fixed cap, so the cap is
 * enforced here rather than at every call site. Batches are requested in
 * parallel and `merge` receives the responses in request order, not completion
 * order. A single failing batch rejects the whole call, leaving callers to
 * decide what a partial view of the data would mean.
 */
export async function batched<I, R>(
  items: I[],
  size: number,
  call: (batch: I[]) => Promise<R>,
  merge: (responses: R[]) => R
): Promise<R> {
  // A size below 1 would never advance the loop and would wedge the event loop
  // rather than fail, and a fractional one would emit batches above the cap.
  if (!Number.isInteger(size) || size < 1) {
    throw new RangeError(`batch size must be a positive integer, got ${size}`)
  }

  const batches: I[][] = []

  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size))
  }

  return merge(await Promise.all(batches.map((batch) => call(batch))))
}
